/**
 * CSI Name Map — Admin view for managing CSI plan name to tier/type mappings.
 *
 * Displays a searchable, sortable table of all CSI name mappings with inline
 * editing via a dialog. Outlier rows are highlighted in an orange/red tint.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  getCsiMappings,
  saveCsiMapping,
  deleteCsiMapping,
  getTiers,
} from '@/services/adminApi';
import type { CsiNameMapping, TierRecord } from '@/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'premier', label: 'Premier' },
  { value: 'boxing', label: 'Boxing' },
];

type SortDirection = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  open: boolean;
  isNew: boolean;
  saving: boolean;
  csiPattern: string;
  tierId: string;
  type: string;
  isOutlier: boolean;
  blockReason: string;
  tiers: TierRecord[];
  onCsiPatternChange: (v: string) => void;
  onTierIdChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onIsOutlierChange: (v: boolean) => void;
  onBlockReasonChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function EditDialog({
  open,
  isNew,
  saving,
  csiPattern,
  tierId,
  type,
  isOutlier,
  blockReason,
  tiers,
  onCsiPatternChange,
  onTierIdChange,
  onTypeChange,
  onIsOutlierChange,
  onBlockReasonChange,
  onSave,
  onCancel,
}: EditDialogProps) {
  const patternValid = csiPattern.trim().length > 0;
  const tierValid = isOutlier || tierId.trim().length > 0;
  const typeValid = isOutlier || type.trim().length > 0;
  const allValid = patternValid && tierValid && typeValid;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isNew ? 'Add CSI Mapping' : 'Edit CSI Mapping'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField
          label="CSI Pattern"
          helperText="The CSI internal plan name or pattern"
          value={csiPattern}
          onChange={(e) => onCsiPatternChange(e.target.value)}
          error={csiPattern.length > 0 && !patternValid}
          size="small"
          fullWidth
          autoFocus
          disabled={!isNew}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isOutlier}
              onChange={(e) => onIsOutlierChange(e.target.checked)}
            />
          }
          label="Is Outlier (employee, legacy, or blocked plan)"
        />
        {!isOutlier && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Tier ID</InputLabel>
              <Select
                value={tierId}
                label="Tier ID"
                onChange={(e) => onTierIdChange(e.target.value)}
              >
                {tiers.map((t) => (
                  <MenuItem key={`${t.region}-${t.tierId}`} value={t.tierId}>
                    {t.displayName} ({t.tierId}) — {t.region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                label="Type"
                onChange={(e) => onTypeChange(e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {isOutlier && (
          <TextField
            label="Block Reason"
            helperText="Explain why self-service is unavailable for this plan"
            value={blockReason}
            onChange={(e) => onBlockReasonChange(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={!allValid || saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------

interface DeleteDialogProps {
  open: boolean;
  pattern: string;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteDialog({ open, pattern, deleting, onConfirm, onCancel }: DeleteDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Mapping</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete the mapping for{' '}
          <strong>{pattern}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={deleting}>
          {deleting ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CsiMap() {
  // Data
  const [mappings, setMappings] = useState<CsiNameMapping[]>([]);
  const [tiers, setTiers] = useState<TierRecord[]>([]);

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search
  const [search, setSearch] = useState('');

  // Sort
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editIsNew, setEditIsNew] = useState(false);
  const [editPattern, setEditPattern] = useState('');
  const [editTierId, setEditTierId] = useState('');
  const [editType, setEditType] = useState('');
  const [editIsOutlier, setEditIsOutlier] = useState(false);
  const [editBlockReason, setEditBlockReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePattern, setDeletePattern] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---------------------------------------------------------------------------
  // Load data
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allMappings, allTiers] = await Promise.all([
        getCsiMappings(),
        getTiers(),
      ]);
      setMappings(allMappings);
      setTiers(allTiers);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
      console.error('CsiMap: failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ---------------------------------------------------------------------------
  // Filtered and sorted data
  // ---------------------------------------------------------------------------

  const filteredMappings = useMemo(() => {
    let result = [...mappings];

    // Filter by search
    if (search.trim()) {
      const lowerSearch = search.toLowerCase();
      result = result.filter((m) =>
        m.csiPattern.toLowerCase().includes(lowerSearch),
      );
    }

    // Sort by csiPattern
    result.sort((a, b) => {
      const cmp = a.csiPattern.localeCompare(b.csiPattern);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [mappings, search, sortDir]);

  // ---------------------------------------------------------------------------
  // Add new mapping
  // ---------------------------------------------------------------------------

  const handleAdd = useCallback(() => {
    setEditIsNew(true);
    setEditPattern('');
    setEditTierId('');
    setEditType('basic');
    setEditIsOutlier(false);
    setEditBlockReason('');
    setEditOpen(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Edit mapping
  // ---------------------------------------------------------------------------

  const handleEdit = useCallback((mapping: CsiNameMapping) => {
    setEditIsNew(false);
    setEditPattern(mapping.csiPattern);
    setEditTierId(mapping.tierId ?? '');
    setEditType(mapping.type ?? 'basic');
    setEditIsOutlier(mapping.isOutlier);
    setEditBlockReason(mapping.blockReason ?? '');
    setEditOpen(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Save handler
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const mapping: CsiNameMapping = {
        csiPattern: editPattern.trim(),
        tierId: editIsOutlier ? null : editTierId,
        type: editIsOutlier ? null : editType,
        isOutlier: editIsOutlier,
        blockReason: editIsOutlier ? editBlockReason.trim() || undefined : undefined,
      };
      await saveCsiMapping(mapping);
      // Update local state
      setMappings((prev) => {
        const idx = prev.findIndex((m) => m.csiPattern === mapping.csiPattern);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = mapping;
          return next;
        }
        return [...prev, mapping];
      });
      setEditOpen(false);
    } catch (err) {
      console.error('CsiMap: failed to save mapping', err);
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  }, [editPattern, editTierId, editType, editIsOutlier, editBlockReason]);

  // ---------------------------------------------------------------------------
  // Delete handlers
  // ---------------------------------------------------------------------------

  const handleDeleteClick = useCallback((pattern: string) => {
    setDeletePattern(pattern);
    setDeleteOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteCsiMapping(deletePattern);
      setMappings((prev) => prev.filter((m) => m.csiPattern !== deletePattern));
      setDeleteOpen(false);
    } catch (err) {
      console.error('CsiMap: failed to delete mapping', err);
      setError(err instanceof Error ? err.message : 'Failed to delete mapping');
    } finally {
      setDeleting(false);
    }
  }, [deletePattern]);

  // ---------------------------------------------------------------------------
  // Sort toggle
  // ---------------------------------------------------------------------------

  const handleSortToggle = useCallback(() => {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        CSI Name Map
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Maps CSI internal plan names to tier IDs and plan types. Outlier plans
        (employee, legacy, etc.) are flagged and blocked from self-service.
      </Typography>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by CSI pattern..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          label={`${filteredMappings.length} of ${mappings.length} mappings`}
          variant="outlined"
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={loading}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 340px)',
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>
                  <TableSortLabel
                    active
                    direction={sortDir}
                    onClick={handleSortToggle}
                  >
                    CSI Pattern
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                  Tier ID
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                  Type
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 90, textAlign: 'center' }}>
                  Outlier
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>
                  Block Reason
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'center' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMappings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      {search ? 'No mappings match your search.' : 'No CSI mappings found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredMappings.map((mapping) => (
                <TableRow
                  key={mapping.csiPattern}
                  hover
                  sx={{
                    backgroundColor: mapping.isOutlier ? '#fff3e0' : 'inherit',
                    '&:hover': {
                      backgroundColor: mapping.isOutlier ? '#ffe0b2' : undefined,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                    >
                      {mapping.csiPattern}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {mapping.tierId ? (
                      <Chip label={mapping.tierId} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        --
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {mapping.type ? (
                      <Chip
                        label={mapping.type.charAt(0).toUpperCase() + mapping.type.slice(1)}
                        size="small"
                        color={
                          mapping.type === 'basic'
                            ? 'primary'
                            : mapping.type === 'premier'
                              ? 'secondary'
                              : 'warning'
                        }
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        --
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {mapping.isOutlier ? (
                      <Chip
                        label="Outlier"
                        size="small"
                        sx={{
                          backgroundColor: '#ff5722',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Chip label="Standard" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {mapping.blockReason || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(mapping)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(mapping.csiPattern)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit dialog */}
      <EditDialog
        open={editOpen}
        isNew={editIsNew}
        saving={saving}
        csiPattern={editPattern}
        tierId={editTierId}
        type={editType}
        isOutlier={editIsOutlier}
        blockReason={editBlockReason}
        tiers={tiers}
        onCsiPatternChange={setEditPattern}
        onTierIdChange={setEditTierId}
        onTypeChange={setEditType}
        onIsOutlierChange={setEditIsOutlier}
        onBlockReasonChange={setEditBlockReason}
        onSave={() => void handleSave()}
        onCancel={() => setEditOpen(false)}
      />

      {/* Delete confirmation dialog */}
      <DeleteDialog
        open={deleteOpen}
        pattern={deletePattern}
        deleting={deleting}
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
}
